/*
 * AmbientCaptureSettings — Non-Technical Setup, Sanctuary-Styled.
 *
 * Replaces the previous "curl + JSON body" wall-of-text with a guided,
 * numbered wizard so non-technical users can enable hands-free capture on
 * iPhone (Siri) or Android (Google Assistant + webhook apps) without
 * touching a single line of code.
 *
 * Structure:
 *   1) Everyday capture (in-app mic reminder) — the easiest option
 *   2) iOS Siri wizard — 6 gentle steps + illustrated setup cards
 *   3) Android / Google wizard — simplest options first, advanced collapsed
 *   4) Your tokens (existing list + revoke)
 *   5) Advanced (collapsible) — raw endpoint + curl snippet for power users
 *
 * All existing API calls, testids, and revealed-once token handling are
 * preserved. The one-shot plaintext dialog remains identical because it's
 * a critical security/UX flow.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import {
  Copy,
  Check,
  KeyRound,
  Mic,
  Plus,
  Trash2,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Info,
  ChevronDown,
  ChevronUp,
  Apple,
  Sparkles,
  Bell,
  Home,
  Headphones,
  Smartphone,
  ExternalLink,
  ArrowRight,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { isIOS } from '../utils/platform';
import { api } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const AMBIENT_ENDPOINT = `${BACKEND_URL}/api/ambient/capture`;

// Optional: a pre-built iCloud Siri Shortcut URL that users can install
// with one tap. When set, the iOS section prominently offers "One-Tap
// Install" instead of the 6-step manual wizard (which remains available
// as an "I'd rather set it up manually" disclosure).
//
// To create this URL, see: /app/docs/CREATE_SIRI_SHORTCUT_ICLOUD.md
// After sharing your shortcut via iCloud, set the resulting
// `https://www.icloud.com/shortcuts/XXXX` URL in the frontend `.env` as:
//   REACT_APP_ICLOUD_SHORTCUT_URL=https://www.icloud.com/shortcuts/XXXX
const ICLOUD_SHORTCUT_URL = (
  process.env.REACT_APP_ICLOUD_SHORTCUT_URL || ''
).trim();
const ONE_TAP_ENABLED = /^https:\/\/(www\.)?icloud\.com\/shortcuts\//i.test(
  ICLOUD_SHORTCUT_URL,
);

// ────────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────────

const CopyableField = ({ value, label, testid, monospace = true }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label || 'Value'} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error('Could not copy — please copy manually.');
    }
  };
  return (
    <div className="flex items-stretch rounded-lg border border-[var(--sd-border)] bg-[var(--sd-surface)] overflow-hidden">
      <pre
        className={`flex-1 p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all text-[var(--sd-fg)] ${monospace ? 'font-mono' : ''}`}
        data-testid={testid}
      >
        {value}
      </pre>
      <button
        onClick={handleCopy}
        className="px-3 border-l border-[var(--sd-border)] text-[var(--sd-muted-fg)] hover:text-[var(--sd-primary)] hover:bg-[var(--sd-surface-hover)] transition-colors"
        aria-label={`Copy ${label || 'value'}`}
        data-testid={`copy-${testid}`}
        type="button"
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
};

const StepCard = ({ number, title, children, testid, icon: Icon = null }) => (
  <div
    className="rounded-xl p-4 sm:p-5"
    style={{
      background: 'rgba(245,245,245,0.04)',
      border: '1px solid rgba(245,245,245,0.10)',
    }}
    data-testid={testid}
  >
    <div className="flex items-start gap-3 mb-2">
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-semibold"
        style={{
          background: 'rgba(183,110,121,0.16)',
          border: '1px solid rgba(183,110,121,0.32)',
          color: '#E1B4BA',
        }}
        aria-hidden
      >
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <h5
          className="text-[14.5px] font-semibold flex items-center gap-2"
          style={{ color: 'var(--sd-fg)' }}
        >
          {Icon && <Icon size={14} className="text-[var(--sd-primary)]" />}
          {title}
        </h5>
      </div>
    </div>
    <div className="text-[13px] leading-relaxed pl-11" style={{ color: 'var(--sd-fg-secondary, #E6E1E2)' }}>
      {children}
    </div>
  </div>
);

const InlineKey = ({ children }) => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[11.5px] font-mono"
    style={{
      background: 'rgba(245,245,245,0.08)',
      border: '1px solid rgba(245,245,245,0.14)',
      color: 'var(--sd-fg)',
    }}
  >
    {children}
  </span>
);

const buildCurlSnippet = (token) => `curl -X POST '${AMBIENT_ENDPOINT}' \\
  -H 'X-Selah-Capture-Token: ${token || 'YOUR_SELAH_TOKEN'}' \\
  -H 'Content-Type: application/json' \\
  -d '{"text":"3am. I feel God whispering about rest.","source":"ifttt"}'`;

const buildSiriBodyExample = () =>
  `text          [Dictated Text variable]
source        siri
device_label  iPhone`;

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────
const AmbientCaptureSettings = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTokenLabel, setNewTokenLabel] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [androidAdvancedOpen, setAndroidAdvancedOpen] = useState(false);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listCaptureTokens();
      setTokens(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('list tokens error', e);
      toast.error('Could not load capture tokens', { description: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleCreate = async (labelOverride = null) => {
    const label = ((labelOverride ?? newTokenLabel) || 'iPhone Siri').trim() || 'iPhone Siri';
    setCreating(true);
    try {
      const res = await api.createCaptureToken(label);
      setRevealed(res);
      setNewTokenLabel('');
      await fetchTokens();
    } catch (e) {
      console.error('create token error', e);
      toast.error('Could not create token', { description: e?.message });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    if (!window.confirm('Revoke this capture token? Any Siri Shortcut or webhook using it will stop working immediately.')) return;
    try {
      await api.revokeCaptureToken(tokenId);
      toast.success('Token revoked');
      setTokens((prev) => prev.filter((t) => t.id !== tokenId));
    } catch (e) {
      toast.error('Could not revoke token', { description: e?.message });
    }
  };

  const copyRevealedAndOpenShortcuts = async () => {
    if (!revealed?.token) return;
    try {
      await navigator.clipboard.writeText(revealed.token);
      toast.success('Token copied to clipboard', {
        description: ONE_TAP_ENABLED
          ? 'Opening the Selah Record shortcut…'
          : 'Opening Shortcuts app…',
      });
    } catch (_) {
      toast.error('Could not copy — please copy manually.');
    }
    // Prefer the pre-built iCloud shortcut URL (opens Shortcuts app with
    // the template pre-loaded). Fall back to the generic `shortcuts://`
    // scheme on devices/configurations without a public URL.
    setTimeout(() => {
      if (ONE_TAP_ENABLED) {
        window.open(ICLOUD_SHORTCUT_URL, '_blank', 'noopener');
      } else {
        window.location.href = 'shortcuts://';
      }
    }, 250);
  };

  const curlSnippet = useMemo(() => buildCurlSnippet(revealed?.token), [revealed]);
  const hasAnyTokens = tokens.length > 0;

  // ────────────────────────────────────────────────────────────
  return (
    <Card
      className="glass-card p-6 sm:p-8"
      data-testid="ambient-capture-settings"
    >
      {/* ── Header ── */}
      <div className="flex items-start gap-3">
        <Mic size={22} className="text-[var(--sd-primary)] mt-0.5 flex-shrink-0" />
        <div>
          <h2
            className="text-[20px] sm:text-[22px] font-semibold"
            style={{
              color: 'var(--sd-fg)',
              fontFamily: '"Playfair Display", serif',
              letterSpacing: '-0.01em',
            }}
          >
            Hands-Free Capture
          </h2>
          <p
            className="text-[13.5px] leading-relaxed mt-1.5 max-w-2xl"
            style={{ color: 'var(--sd-muted-fg)' }}
          >
            {isIOS() ? (
              <>
                Push downloads into Selah from Siri or any webhook — without
                opening the app. Pick the path that feels easiest for you.
              </>
            ) : (
              <>
                Push downloads into Selah from Siri, Google Assistant, or any
                webhook — without opening the app. Pick the path that feels
                easiest for you.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Privacy note */}
      <div
        className="mt-4 flex items-start gap-2 rounded-lg p-3"
        style={{
          background: 'rgba(245,245,245,0.04)',
          border: '1px solid rgba(245,245,245,0.10)',
        }}
      >
        <ShieldAlert size={16} className="text-[var(--sd-primary)] mt-0.5 flex-shrink-0" />
        <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--sd-muted-fg)' }}>
          <span style={{ color: 'var(--sd-fg)', fontWeight: 500 }}>A gentle note:</span>{' '}
          Ambient captures live in plaintext on our server until you open the
          app and review them. When you tap <em>Save to Journey</em>, they’re
          encrypted on your device and moved into your zero-knowledge downloads.
          Discarded captures are removed immediately.
        </p>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 1) Easiest: in-app mic reminder                            */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section className="mt-8" data-testid="capture-easiest-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-[var(--sd-primary)]" />
          <h3
            className="text-[15.5px] font-semibold"
            style={{ color: 'var(--sd-fg)' }}
          >
            The easiest way — already built in
          </h3>
        </div>
        <div
          className="rounded-xl p-4 sm:p-5"
          style={{
            background:
              'linear-gradient(180deg, rgba(183,110,121,0.06) 0%, rgba(28,30,33,0.20) 100%)',
            border: '1px solid rgba(183,110,121,0.22)',
          }}
        >
          <p className="text-[13.5px] leading-relaxed" style={{ color: 'var(--sd-fg-secondary, #E6E1E2)' }}>
            On your Dashboard there’s a <strong>Speak a Download</strong> mic
            button. Tap it, speak, review — and the capture lands in your{' '}
            <em>Unprocessed Downloads</em> tray. No setup, no tokens, no shortcuts.
          </p>
          <p
            className="mt-2 text-[12px]"
            style={{ color: 'var(--sd-muted-fg)' }}
          >
            Works on iPhone (Safari / PWA) and Android (Chrome). If you want
            hands-free capture from a locked phone, keep reading.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 2) iOS Siri wizard                                         */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section className="mt-10" data-testid="capture-ios-section">
        <div className="flex items-center gap-2 mb-4">
          <Apple size={16} className="text-[var(--sd-primary)]" />
          <h3
            className="text-[15.5px] font-semibold"
            style={{ color: 'var(--sd-fg)' }}
          >
            iPhone / iPad — Siri Shortcut
          </h3>
        </div>

        <p
          className="text-[13px] leading-relaxed mb-4 max-w-3xl"
          style={{ color: 'var(--sd-muted-fg)' }}
        >
          Once set up, you can say <em>&ldquo;Hey Siri, Selah Record&rdquo;</em> from a
          locked phone, AirPods, Apple Watch, or CarPlay — dictate — and
          your download lands in your inbox.
        </p>

        {/* ── One-Tap Install banner (only when a public iCloud URL is
             configured; see /app/docs/CREATE_SIRI_SHORTCUT_ICLOUD.md) ── */}
        {ONE_TAP_ENABLED && (
          <div
            className="rounded-xl p-4 sm:p-5 mb-5"
            style={{
              background:
                'linear-gradient(180deg, rgba(183,110,121,0.10) 0%, rgba(28,30,33,0.20) 100%)',
              border: '1px solid rgba(183,110,121,0.30)',
              boxShadow: '0 8px 24px -14px rgba(183,110,121,0.45)',
            }}
            data-testid="ios-one-tap-banner"
          >
            <div className="flex items-start gap-3 mb-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'rgba(183,110,121,0.20)',
                  border: '1px solid rgba(183,110,121,0.35)',
                }}
                aria-hidden
              >
                <Sparkles size={16} style={{ color: '#E1B4BA' }} />
              </div>
              <div>
                <p
                  className="text-[14.5px] font-semibold"
                  style={{ color: 'var(--sd-fg)' }}
                >
                  One-tap install — takes about 30 seconds
                </p>
                <p
                  className="text-[12.5px] mt-1 leading-relaxed"
                  style={{ color: 'var(--sd-muted-fg)' }}
                >
                  We&apos;ll generate your private token, copy it to your
                  clipboard, then open the pre-built Selah Record shortcut in
                  the Shortcuts app. When Shortcuts asks for your token, just
                  paste it. Done.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={async () => {
                  // Generate a fresh token if none exists, then guide install.
                  if (!revealed && tokens.length === 0) {
                    await handleCreate('iPhone Siri');
                    // The reveal dialog will pop up with a "Copy & open
                    // Shortcuts" action that includes the iCloud URL launch.
                    return;
                  }
                  if (revealed?.token) {
                    // Already have a fresh token — copy + open iCloud.
                    try {
                      await navigator.clipboard.writeText(revealed.token);
                      toast.success('Token copied — opening Shortcuts…', {
                        description: 'Paste it when Shortcuts asks.',
                      });
                    } catch (_) {
                      toast.error('Could not copy — please copy manually.');
                    }
                    setTimeout(() => {
                      window.open(ICLOUD_SHORTCUT_URL, '_blank', 'noopener');
                    }, 250);
                    return;
                  }
                  // Token exists but we don't have plaintext (past session);
                  // ask the user to generate a new one so they can copy it.
                  toast.info('Generate a fresh token to install', {
                    description:
                      'For security we can only show the token plaintext once, so we\'ll create a new one now.',
                  });
                  await handleCreate('iPhone Siri');
                }}
                disabled={creating}
                data-testid="ios-one-tap-install-button"
                className="text-[13.5px] font-semibold flex-1"
                style={{
                  background: 'linear-gradient(180deg, #D7A1A8 0%, #B76E79 100%)',
                  color: '#1C1C1C',
                  border: '1px solid #B76E79',
                  boxShadow: '0 8px 22px -10px rgba(183,110,121,0.55)',
                }}
              >
                {creating ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    <ExternalLink size={14} className="mr-2" />
                    One-tap install on my iPhone
                  </>
                )}
              </Button>
              <a
                href={ICLOUD_SHORTCUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12.5px] inline-flex items-center justify-center px-3 py-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(183,110,121,0.5)]"
                style={{
                  color: 'var(--sd-fg)',
                  border: '1px solid rgba(245,245,245,0.14)',
                  background: 'rgba(245,245,245,0.04)',
                }}
                data-testid="ios-one-tap-preview-link"
              >
                Preview shortcut
              </a>
            </div>
            <p className="mt-3 text-[11.5px]" style={{ color: 'var(--sd-muted-fg)' }}>
              Prefer to set it up yourself? The step-by-step wizard is right below.
            </p>
          </div>
        )}

        {/* Quick token action (fallback / for users who want manual control) */}
        <div
          className="rounded-xl p-4 sm:p-5 mb-5"
          style={{
            background: 'rgba(245,245,245,0.05)',
            border: '1px solid rgba(245,245,245,0.12)',
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 justify-between">
            <div>
              <p
                className="text-[14px] font-semibold"
                style={{ color: 'var(--sd-fg)' }}
              >
                {ONE_TAP_ENABLED
                  ? 'Or, set it up manually — create your iPhone token'
                  : 'Start here — create your iPhone token'}
              </p>
              <p
                className="text-[12.5px] mt-1 leading-relaxed"
                style={{ color: 'var(--sd-muted-fg)' }}
              >
                We’ll generate a private token and copy it to your clipboard.
                You only see it once.
              </p>
            </div>
            <Button
              onClick={() => handleCreate('iPhone Siri')}
              disabled={creating}
              data-testid="ios-generate-token-button"
              className="whitespace-nowrap"
              style={{
                background: ONE_TAP_ENABLED
                  ? 'rgba(245,245,245,0.08)'
                  : 'linear-gradient(180deg, #D7A1A8 0%, #B76E79 100%)',
                color: ONE_TAP_ENABLED ? '#F2F2F2' : '#1C1C1C',
                border: ONE_TAP_ENABLED
                  ? '1px solid rgba(245,245,245,0.14)'
                  : '1px solid #B76E79',
                boxShadow: ONE_TAP_ENABLED
                  ? 'none'
                  : '0 8px 22px -10px rgba(183,110,121,0.55)',
              }}
            >
              {creating ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <KeyRound size={14} className="mr-2" />
                  Generate my iPhone token
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Six-step wizard */}
        <div className="grid grid-cols-1 gap-3">
          <StepCard
            number="1"
            title="Open the Shortcuts app on your iPhone"
            icon={Smartphone}
            testid="ios-step-1"
          >
            It’s an Apple built-in app called <strong>Shortcuts</strong>. If
            you can’t find it, swipe down and search for &ldquo;Shortcuts&rdquo;.
          </StepCard>

          <StepCard
            number="2"
            title="Create a new Shortcut named ‘Selah Record’"
            icon={Plus}
            testid="ios-step-2"
          >
            Tap the <InlineKey>+</InlineKey> in the top corner, then rename it
            &ldquo;Selah Record&rdquo; at the top.
          </StepCard>

          <StepCard
            number="3"
            title="Add the ‘Dictate Text’ action"
            icon={Mic}
            testid="ios-step-3"
          >
            In the search bar at the bottom, type <em>Dictate Text</em> and tap
            to add it. Set language to your preference.
          </StepCard>

          <StepCard
            number="4"
            title="Add the ‘Get Contents of URL’ action"
            icon={ArrowRight}
            testid="ios-step-4"
          >
            Search <em>Get Contents of URL</em> and add it. Then fill in:
            <ul className="mt-3 space-y-1.5 list-disc list-inside">
              <li>
                <strong>URL:</strong>{' '}
                <InlineKey>{AMBIENT_ENDPOINT}</InlineKey>
              </li>
              <li>
                <strong>Method:</strong> <InlineKey>POST</InlineKey>
              </li>
              <li>
                <strong>Headers</strong> → tap &ldquo;Add new header&rdquo;:
                <ul className="mt-1 ml-4 list-disc list-inside">
                  <li>Key: <InlineKey>X-Selah-Capture-Token</InlineKey></li>
                  <li>Value: <InlineKey>your selah_… token</InlineKey></li>
                </ul>
              </li>
              <li>
                <strong>Request Body</strong> → select{' '}
                <InlineKey>JSON</InlineKey>, then add these three fields:
              </li>
            </ul>
            <div className="mt-3">
              <CopyableField
                value={buildSiriBodyExample()}
                label="Siri body"
                testid="ios-siri-body"
              />
            </div>
            <p className="mt-2 text-[12px] italic" style={{ color: 'var(--sd-muted-fg)' }}>
              <Info size={12} className="inline mr-1 -mt-0.5" />
              For the <InlineKey>text</InlineKey> value, tap the field and
              choose the <strong>Dictated Text</strong> Magic Variable (a blue
              chip) — don’t type the words &ldquo;Dictated Text&rdquo; by
              hand.
            </p>
          </StepCard>

          <StepCard
            number="5"
            title="Add ‘Show Notification’ for a gentle confirmation"
            icon={Bell}
            testid="ios-step-5"
          >
            Search <em>Show Notification</em> and add it. Set:
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>
                <strong>Title:</strong>{' '}
                <InlineKey>Selah Received</InlineKey>
              </li>
              <li>
                <strong>Body:</strong> tap the field → <em>Select Variable</em>{' '}
                → choose <strong>Dictated Text</strong> (blue chip).
              </li>
              <li>
                Leave <strong>Attachment</strong> and <strong>Sound</strong> alone.
              </li>
            </ul>
            <p className="mt-2 text-[12px] italic" style={{ color: 'var(--sd-muted-fg)' }}>
              This replaces the raw JSON popup with a soft banner so you know it
              worked.
            </p>
          </StepCard>

          <StepCard
            number="6"
            title="Save, then try it"
            icon={Check}
            testid="ios-step-6"
          >
            Tap the <InlineKey>▶︎</InlineKey> Play button in the
            editor to test right there — dictate a short thought. If it
            works, say{' '}
            <em>&ldquo;Hey Siri, Selah Record&rdquo;</em> from anywhere. Your
            capture will appear in your Dashboard’s{' '}
            <strong>Unprocessed Downloads</strong> tray within 45 seconds (or
            sooner if you tap refresh).
          </StepCard>
        </div>

        {/* ── Extras: AirPods / Home Screen / Lock Screen ── */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ExtraCard
            icon={Headphones}
            title="AirPods & CarPlay"
            body="Any Siri-enabled device works with the same shortcut. Just enable Use with Siri in your shortcut’s settings."
          />
          <ExtraCard
            icon={Home}
            title="Home Screen icon"
            body="Open the shortcut’s info panel → tap the pin-to-home option → pick a custom Selah icon."
          />
          <ExtraCard
            icon={Sparkles}
            title="Lock Screen widget"
            body="iOS 16+ can pin your shortcut to the Lock Screen — one tap, Face ID unlocks, dictation starts."
          />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 3) Android / Google                                        */}
      {/*                                                            */}
      {/* Inside the iOS App Store build we suppress this entire     */}
      {/* section — it contains PWA-install instructions ("Install   */}
      {/* Selah as a PWA", Chrome menu, Google Assistant, etc.) that */}
      {/* would be flagged by App Review as either steering (3.1.1)  */}
      {/* or off-topic app-completeness clutter (2.1(b)).            */}
      {/* ══════════════════════════════════════════════════════════ */}
      {!isIOS() && (
      <section className="mt-12" data-testid="capture-android-section">
        <div className="flex items-center gap-2 mb-4">
          <Smartphone size={16} className="text-[var(--sd-primary)]" />
          <h3
            className="text-[15.5px] font-semibold"
            style={{ color: 'var(--sd-fg)' }}
          >
            Android / Google Assistant
          </h3>
        </div>

        <p
          className="text-[13px] leading-relaxed mb-4 max-w-3xl"
          style={{ color: 'var(--sd-muted-fg)' }}
        >
          Android doesn’t ship with a native shortcut system as flexible
          as Apple’s. Here’s what we recommend, from easiest to most
          powerful.
        </p>

        <div className="grid grid-cols-1 gap-3">
          <StepCard
            number="A"
            title="Best default — use the in-app mic"
            icon={Mic}
            testid="android-option-a"
          >
            Open Selah in Chrome or your installed PWA and use the{' '}
            <strong>Speak a Download</strong> button on your Dashboard. It
            works instantly and needs no setup.
          </StepCard>

          <StepCard
            number="B"
            title="One-tap widget — Chrome PWA shortcut"
            icon={Home}
            testid="android-option-b"
          >
            Install Selah as a PWA (Chrome menu → <em>Install app</em>),
            then long-press its icon on your Home Screen. Some Android skins
            support &ldquo;jump to&rdquo; shortcuts that open the record page
            in one tap.
          </StepCard>

          <StepCard
            number="C"
            title="Hands-free voice — Google Assistant Routine + webhook"
            icon={Sparkles}
            testid="android-option-c"
          >
            Google Assistant doesn’t POST to webhooks directly, but you
            can chain it through <strong>IFTTT</strong> or <strong>Zapier</strong>{' '}
            (see Advanced below). Tap or hold your Assistant, say a trigger
            phrase, and let IFTTT forward the transcription to Selah.
          </StepCard>
        </div>

        {/* Advanced Android options */}
        <button
          type="button"
          onClick={() => setAndroidAdvancedOpen((v) => !v)}
          className="mt-4 inline-flex items-center gap-2 text-[12.5px] rounded-lg px-3 py-2 transition-colors"
          style={{
            color: 'var(--sd-fg)',
            border: '1px solid rgba(245,245,245,0.14)',
            background: 'rgba(245,245,245,0.04)',
          }}
          data-testid="android-advanced-toggle"
        >
          {androidAdvancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {androidAdvancedOpen ? 'Hide advanced Android setup' : 'Show advanced Android setup'}
        </button>

        {androidAdvancedOpen && (
          <div
            className="mt-4 rounded-xl p-4 sm:p-5 space-y-4"
            style={{
              background: 'rgba(245,245,245,0.04)',
              border: '1px solid rgba(245,245,245,0.10)',
            }}
            data-testid="android-advanced-panel"
          >
            <div>
              <h5 className="text-[13.5px] font-semibold mb-1.5" style={{ color: 'var(--sd-fg)' }}>
                IFTTT / Zapier / Make.com Webhook
              </h5>
              <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--sd-muted-fg)' }}>
                Create an applet with a voice trigger. As the action, use{' '}
                <em>Webhooks</em> configured like this:
              </p>
              <ul className="mt-2 list-disc list-inside text-[12.5px]" style={{ color: 'var(--sd-fg-secondary, #E6E1E2)' }}>
                <li>URL: <InlineKey>{AMBIENT_ENDPOINT}</InlineKey></li>
                <li>Method: <InlineKey>POST</InlineKey></li>
                <li>Header: <InlineKey>X-Selah-Capture-Token: selah_…</InlineKey></li>
                <li>Content-Type: <InlineKey>application/json</InlineKey></li>
                <li>Body: <InlineKey>{'{"text":"...","source":"ifttt"}'}</InlineKey></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[13.5px] font-semibold mb-1.5" style={{ color: 'var(--sd-fg)' }}>
                Tasker (Android power users)
              </h5>
              <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--sd-muted-fg)' }}>
                Create a Tasker profile with a voice command trigger and an{' '}
                <em>HTTP Request</em> action using the same URL, method, header,
                and JSON body as above. Tasker also supports offline transcription.
              </p>
            </div>
          </div>
        )}
      </section>
      )}

      <Separator className="my-10" />

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 4) Your tokens                                             */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section data-testid="capture-tokens-section">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3
            className="text-[15.5px] font-semibold flex items-center gap-2"
            style={{ color: 'var(--sd-fg)' }}
          >
            <ShieldCheck size={15} className="text-[var(--sd-primary)]" />
            Your capture tokens
          </h3>
          {hasAnyTokens && (
            <Badge
              className="text-[10.5px]"
              style={{
                background: 'rgba(245,245,245,0.06)',
                color: 'var(--sd-muted-fg)',
                border: '1px solid rgba(245,245,245,0.14)',
              }}
              data-testid="capture-tokens-count-badge"
            >
              {tokens.length} active
            </Badge>
          )}
        </div>

        <p
          className="text-[12.5px] leading-relaxed mb-4 max-w-2xl"
          style={{ color: 'var(--sd-muted-fg)' }}
        >
          Each token is a private key for one integration (e.g. iPhone Siri,
          IFTTT). Revoke any token to instantly stop that integration —
          the token cannot read, edit, or delete your data.
        </p>

        {/* Manual add row (advanced users, custom labels) */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input
            placeholder="Optional label (e.g. Home Automation, Alexa Skill)"
            value={newTokenLabel}
            onChange={(e) => setNewTokenLabel(e.target.value)}
            className="flex-1"
            maxLength={60}
            data-testid="new-token-label-input"
          />
          <Button
            onClick={() => handleCreate()}
            disabled={creating}
            variant="secondary"
            className="text-[13px]"
            data-testid="create-capture-token-button"
            style={{
              background: 'rgba(245,245,245,0.06)',
              color: 'var(--sd-fg)',
              border: '1px solid rgba(245,245,245,0.14)',
            }}
          >
            {creating ? (
              <Loader2 size={14} className="mr-2 animate-spin" />
            ) : (
              <Plus size={14} className="mr-2" />
            )}
            Add token
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--sd-muted-fg)]">
            <Loader2 size={14} className="animate-spin" />
            Loading tokens…
          </div>
        ) : tokens.length === 0 ? (
          <p
            className="text-[13px] italic"
            style={{ color: 'var(--sd-muted-fg)' }}
            data-testid="capture-tokens-empty"
          >
            No tokens yet. Generate one above (or use the iPhone wizard) to
            enable Siri / IFTTT / Zapier.
          </p>
        ) : (
          <div className="space-y-2">
            {tokens.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg"
                style={{
                  background: 'rgba(245,245,245,0.04)',
                  border: '1px solid rgba(245,245,245,0.10)',
                }}
                data-testid={`capture-token-row-${t.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-medium text-[13.5px] truncate"
                      style={{ color: 'var(--sd-fg)' }}
                    >
                      {t.label}
                    </span>
                    <Badge
                      className="text-[10px]"
                      style={{
                        background: 'rgba(245,245,245,0.06)',
                        color: 'var(--sd-muted-fg)',
                        border: '1px solid rgba(245,245,245,0.14)',
                      }}
                    >
                      selah_…{t.id.slice(-6)}
                    </Badge>
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--sd-muted-fg)' }}>
                    Created {format(new Date(t.created_at), 'MMM d, yyyy')}
                    {t.last_used_at ? (
                      <> · last used {formatDistanceToNow(new Date(t.last_used_at), { addSuffix: true })}</>
                    ) : (
                      <> · never used</>
                    )}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevoke(t.id)}
                  className="text-[12.5px]"
                  style={{ color: 'var(--sd-muted-fg)' }}
                  data-testid={`revoke-token-${t.id}`}
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <Separator className="my-10" />

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 5) Advanced — curl / raw endpoint                          */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section data-testid="capture-advanced-section">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-[13px] rounded-lg px-3 py-2 transition-colors"
          style={{
            color: 'var(--sd-fg)',
            border: '1px solid rgba(245,245,245,0.14)',
            background: 'rgba(245,245,245,0.04)',
          }}
          data-testid="advanced-toggle-button"
        >
          <BookOpen size={14} />
          {advancedOpen ? 'Hide advanced developer setup' : 'Show advanced developer setup'}
          {advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {advancedOpen && (
          <div className="mt-5 space-y-5" data-testid="advanced-panel">
            <div>
              <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'var(--sd-muted-fg)' }}>
                Your ambient endpoint
              </p>
              <CopyableField
                value={AMBIENT_ENDPOINT}
                label="Endpoint URL"
                testid="ambient-endpoint-value"
              />
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'var(--sd-muted-fg)' }}>
                Example: curl
              </p>
              <CopyableField
                value={curlSnippet}
                label="curl example"
                testid="curl-example"
              />
            </div>

            <div
              className="rounded-lg p-3 text-[12px] leading-relaxed"
              style={{
                background: 'rgba(245,245,245,0.04)',
                border: '1px solid rgba(245,245,245,0.10)',
                color: 'var(--sd-muted-fg)',
              }}
            >
              <strong style={{ color: 'var(--sd-fg)' }}>Auth options:</strong>
              {' '}This endpoint accepts either{' '}
              <InlineKey>X-Selah-Capture-Token: selah_…</InlineKey> (external
              integrations) or <InlineKey>Authorization: Bearer &lt;jwt&gt;</InlineKey>{' '}
              (in-app clients). Text must be 3–6000 characters. Captures are
              analyzed with GPT and returned with themes, category, verse, and
              a personalized prayer.
            </div>
          </div>
        )}
      </section>

      {/* ── Reveal-once dialog (unchanged from before) ── */}
      <AlertDialog
        open={!!revealed}
        onOpenChange={(open) => !open && setRevealed(null)}
      >
        <AlertDialogTrigger asChild>
          <span />
        </AlertDialogTrigger>
        <AlertDialogContent
          className="max-w-lg glass-card"
          data-testid="reveal-token-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="flex items-center gap-2"
              style={{ color: 'var(--sd-fg)' }}
            >
              <KeyRound size={18} className="text-[var(--sd-primary)]" />
              Copy your capture token
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-[13px] leading-relaxed"
              style={{ color: 'var(--sd-muted-fg)' }}
            >
              This is the ONLY time you’ll see the full token. Copy it now
              into your Siri Shortcut, IFTTT recipe, or a password manager. If
              you lose it, generate a fresh one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {revealed && (
            <div className="space-y-3">
              <div>
                <p
                  className="text-xs mb-1.5"
                  style={{ color: 'var(--sd-muted-fg)' }}
                >
                  Label:{' '}
                  <span style={{ color: 'var(--sd-fg)' }}>{revealed.label}</span>
                </p>
                <CopyableField
                  value={revealed.token}
                  label="Capture token"
                  testid="revealed-token"
                />
              </div>
              <div
                className="rounded-lg p-3 text-[12px] leading-relaxed"
                style={{
                  background: 'rgba(183,110,121,0.10)',
                  border: '1px solid rgba(183,110,121,0.30)',
                  color: 'var(--sd-fg-secondary, #E6E1E2)',
                }}
              >
                Keep this secret. Anyone with this token can add captures to
                your Unprocessed Downloads tray. It cannot read, edit, or
                delete your data.
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={copyRevealedAndOpenShortcuts}
              data-testid="reveal-token-copy-and-open"
              style={{
                background: 'linear-gradient(180deg, #D7A1A8 0%, #B76E79 100%)',
                color: '#1C1C1C',
              }}
            >
              <ExternalLink size={14} className="mr-2" />
              Copy &amp; open Shortcuts
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const ExtraCard = ({ icon: Icon, title, body }) => (
  <div
    className="rounded-xl p-4"
    style={{
      background: 'rgba(245,245,245,0.04)',
      border: '1px solid rgba(245,245,245,0.10)',
    }}
  >
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={14} className="text-[var(--sd-primary)]" />
      <h5 className="text-[13.5px] font-semibold" style={{ color: 'var(--sd-fg)' }}>
        {title}
      </h5>
    </div>
    <p className="text-[12px] leading-relaxed" style={{ color: 'var(--sd-muted-fg)' }}>
      {body}
    </p>
  </div>
);

export default AmbientCaptureSettings;
